import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { ChartTooltip } from "@/components/charts/ChartTooltip";
import { C, axisProps } from "@/lib/chart";
import { axisINR } from "@/lib/format";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/common/ui";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell, LabelList,
} from "recharts";
import { Zap, Eye, MousePointerClick } from "lucide-react";
import { SpendListSheet } from "@/components/dashboard/SpendListSheet";

const pctOf = (v, total) => (total ? Math.round((v / total) * 100) : 0);

function SplitChart({ title, subtitle, icon: Icon, accent, gradId, rows, money, testId, onBarClick }) {
  const data = (rows || []).map((r) => ({ name: r.name || r.project || r.label || r.vertical, spend: r.spend }));
  const total = data.reduce((s, d) => s + (d.spend || 0), 0);
  const tip = (v) => `${money(v)} \u00b7 ${pctOf(v, total)}%`;
  return (
    <div className="group rounded-xl border bg-card p-4 transition-colors duration-150 hover:border-[hsl(var(--border))]" data-testid={testId}>
      <div className="mb-1 flex items-center justify-between">
        <span className="flex items-center gap-2 text-sm font-semibold">
          <span className="flex h-7 w-7 items-center justify-center rounded-lg" style={{ background: `${accent}1f`, color: accent }}>
            <Icon className="h-4 w-4" />
          </span>
          {title}
        </span>
        <span className="flex items-center gap-2">
          {onBarClick && <span className="flex items-center gap-1 text-[10px] text-muted-foreground"><MousePointerClick className="h-3 w-3" /> click a bar for the spend list</span>}
          <span className="num text-sm font-semibold tabular-nums" data-testid={`${testId}-total`}>{money(total)}</span>
        </span>
      </div>
      <p className="mb-2 text-[11px] text-muted-foreground">{subtitle}</p>
      {data.length === 0 ? (
        <div className="py-8"><EmptyState title="No spend" message="No spend recorded for this scope." /></div>
      ) : (
        <div style={{ height: Math.max(200, data.length * 34) }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} layout="vertical" margin={{ top: 2, right: 60, left: 4, bottom: 0 }}>
              <defs>
                <linearGradient id={gradId} x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor={accent} stopOpacity={0.55} />
                  <stop offset="100%" stopColor={accent} stopOpacity={1} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={C.grid} horizontal={false} />
              <XAxis type="number" tickFormatter={axisINR} {...axisProps} />
              <YAxis type="category" dataKey="name" width={104} tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 10 }} tickLine={false} axisLine={{ stroke: "hsl(var(--border))" }} interval={0} />
              <Tooltip content={<ChartTooltip formatter={tip} />} cursor={{ fill: "hsl(var(--surface-2))" }} />
              <Bar dataKey="spend" name="Spend" radius={[0, 5, 5, 0]} barSize={16} isAnimationActive={false} fill={`url(#${gradId})`}
                cursor={onBarClick ? "pointer" : "default"} onClick={(d) => onBarClick && d && onBarClick(d.name)}>
                {data.map((_, i) => <Cell key={i} />)}
                <LabelList dataKey="spend" position="right" formatter={(v) => money(v)}
                  style={{ fill: "hsl(var(--muted-foreground))", fontSize: 10 }} className="tabular-nums" />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

/**
 * Project Spends split into two charts — Performance vs Visibility — shown when
 * more than one project is in scope. (Hidden by the parent for a single project.)
 */
export function SpendsSplitCharts({ period = "FY", projects = [] }) {
  const { money } = useCurrency();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drill, setDrill] = useState(null);
  const projKey = (projects || []).join(",");

  useEffect(() => {
    let on = true; setLoading(true);
    const params = { period };
    if (projKey) params.projects = projKey;
    api.spendsByVertical(params)
      .then((d) => { if (on) { setData(d); setLoading(false); } })
      .catch(() => on && setLoading(false));
    return () => { on = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, projKey]);

  const split = data?.by_project_split || { performance: [], visibility: [] };

  return (
    <div className="border-t p-4" data-testid="spends-split-charts">
      <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Project spends — Performance vs Visibility</p>
      {loading && !data ? (
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2" data-testid="spends-split-loading">
          <Skeleton className="h-56 rounded-xl" />
          <Skeleton className="h-56 rounded-xl" />
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
          <SplitChart
            title="Performance Spends" subtitle="Lead-generating spend by project — click a bar to see the categorised spend list"
            icon={Zap} accent="hsl(var(--chart-cat-3))" gradId="split-grad-perf" rows={split.performance} money={money}
            testId="spends-split-performance"
            onBarClick={(name) => setDrill({ title: `${name} — Performance spends`, subtitle: "grouped by vertical", filters: { vis_perf: "Performance", project: name } })}
          />
          <SplitChart
            title="Visibility Spends" subtitle="Brand / awareness spend by project — click a bar to see the categorised spend list"
            icon={Eye} accent="hsl(var(--chart-cat-1))" gradId="split-grad-vis" rows={split.visibility} money={money}
            testId="spends-split-visibility"
            onBarClick={(name) => setDrill({ title: `${name} — Visibility spends`, subtitle: "grouped by vertical", filters: { vis_perf: "Visibility", project: name } })}
          />
        </div>
      )}
      <SpendListSheet drill={drill} period={period} onClose={() => setDrill(null)} />
    </div>
  );
}
